import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useUpdateWorkspace } from '@/api/workspaces';
import type { Workspace, WorkspacePlan } from '@/api/types';
import { Button, Input, Modal, Select, useToast } from '@/components/ui';
import { humanize } from '@/lib/format';
import { PLANS } from './meta';

const schema = z.object({
  name: z.string().trim().min(2, 'At least 2 characters').max(120),
  plan: z.enum(['trial', 'starter', 'growth', 'enterprise']),
  contactName: z.string().trim().max(120),
  contactEmail: z.string().trim().email('Enter a valid email'),
  website: z.string().trim().url('Enter a full URL').or(z.literal('')),
});
type FormValues = z.infer<typeof schema>;

/** Super admin: a client's profile and plan, plus suspend / reactivate. */
export function EditClientModal({ client, onClose }: { client: Workspace; onClose: () => void }) {
  const toast = useToast();
  const update = useUpdateWorkspace();
  const suspended = client.status === 'suspended';
  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: client.name,
      plan: client.plan,
      contactName: client.contactName,
      contactEmail: client.contactEmail,
      website: client.website ?? '',
    },
  });

  const onSubmit = handleSubmit((v) =>
    update.mutate(
      {
        id: client.id,
        input: { ...v, plan: v.plan as WorkspacePlan, website: v.website || null },
      },
      {
        onSuccess: () => (toast.success('Client updated'), onClose()),
        onError: (e) => toast.error(e.message),
      },
    ),
  );

  const toggleStatus = () =>
    update.mutate(
      { id: client.id, input: { status: suspended ? 'active' : 'suspended' } },
      {
        onSuccess: () => {
          toast.success(
            suspended ? `${client.name} reactivated` : `${client.name} suspended — its team is signed out`,
          );
          onClose();
        },
        onError: (e) => toast.error(e.message),
      },
    );

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title={`Edit ${client.name}`}
      description={`Workspace handle: ${client.slug}`}
      footer={
        <>
          <Button
            variant={suspended ? 'secondary' : 'danger'}
            className="mr-auto"
            loading={update.isPending && !isDirty}
            onClick={toggleStatus}
          >
            {suspended ? 'Reactivate' : 'Suspend client'}
          </Button>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="edit-client-form"
            loading={update.isPending && isDirty}
            disabled={!isDirty}
          >
            Save changes
          </Button>
        </>
      }
    >
      <form id="edit-client-form" onSubmit={onSubmit} noValidate className="grid gap-4 sm:grid-cols-2">
        <Input label="Client name" required error={errors.name?.message} {...register('name')} />
        <Select label="Plan" {...register('plan')}>
          {PLANS.map((p) => (
            <option key={p} value={p}>
              {humanize(p)}
            </option>
          ))}
        </Select>
        <Input label="Contact name" error={errors.contactName?.message} {...register('contactName')} />
        <Input
          label="Contact email"
          type="email"
          error={errors.contactEmail?.message}
          {...register('contactEmail')}
        />
        <Input
          label="Website"
          error={errors.website?.message}
          {...register('website')}
          wrapperClassName="sm:col-span-2"
        />
      </form>
    </Modal>
  );
}
